if (!customElements.get('rim-customizer')) {
  customElements.define(
    'rim-customizer',
    class RimCustomizer extends HTMLElement {
      constructor() {
        super();
        
        this.selectedVehicle = {};
        this.selectedWheel = null;
        this.selectedTires = { front: null, rear: null };
        this.selectedOptions = {};
        this.selectedRimColor = '';
        this.selectedHubColor = '';
        this.selectedWheelColor = '';
        this.selectedStyle = ''; // Will be generated from color selections
        this.rimColors = [];
        this.hubColors = [];
        this.wheelColors = [];
        this.selectedTireOption = 'Tusk T35\'s';
        this.quantity = 1;
        this.totalPrice = 0;
        this.uploadedLogo = null;
        this.selectedWheelPosition = null; // 'front' or 'rear' for single wheel
        this.customizeBoth = false; // true if wheel set and customize both is checked
        this.availableProducts = []; // Store products from Shopify collection
        this.matchedProduct = null; // Currently matched product based on style
        
        this.fitmentData = {
          "Surron": {
            "Light Bee X/S": ["2019", "2020", "2021", "2022", "2023", "2024"],
            "Ultra Bee": ["2023", "2024"],
            "Storm Bee": ["2024"]
          },
          "Talaria": {
            "X3 (XXX)": ["2022", "2023", "2024"],
            "MX3": ["2023", "2024"],
            "Sting MX4": ["2024"],
            "Sting MX5 Pro": ["2024"]
          },
          "E Ride Pro": {
            "Mini": ["2023", "2024"],
            "S": ["2023", "2024"],
            "SS 2.0": ["2023", "2024"],
            "SS 3.0": ["2024"],
            "SR": ["2024"]
          },
          "Stark Varg": {
            "MX": ["2023", "2024"],
            "EX": ["2024"]
          },
          "Arctic Leopard": {
            "EXE 800": ["2023", "2024"],
            "EXE 880": ["2024"],
            "EX 700": ["2023", "2024"],
            "EX 800": ["2023", "2024"],
            "XE Pro": ["2024"],
            "XE Pro S": ["2024"]
          }
        };

        // Map style options to image paths - will be populated from Liquid template
        this.styleImages = window.rimImageUrls || {};

        this.tireOptions = [
          { name: 'None', price: 0, description: 'Wheelset only' },
          { name: 'Tusk T35\'s', price: 95, description: 'Front: 80/100-16, Rear: 100/90-19' },
          { name: 'Shinko 241\'s', price: 90, description: 'Front: 80/100-16, Rear: 100/90-19' },
          { name: '525 Cheater (rear) and Dunlop MX53 (front)', price: 120, description: 'Front: 80/100-16, Rear: 100/90-19' },
          { name: 'Bridgestone X30\'s', price: 110, description: 'Front: 80/100-16, Rear: 100/90-19' },
          { name: 'Maxxis MaxxCross', price: 115, description: 'Front: 80/100-16, Rear: 100/90-19' }
        ];
        
        this.init();
      }
      
      /**
       * Display an error message in a container
       * @param {HTMLElement} container - Container element to show error in
       * @param {string} message - Error message text
       * @param {string} className - Optional CSS class (default: 'error-message-wrapper')
       */
      showError(container, message, className = 'error-message-wrapper') {
        if (!container) return;
        container.innerHTML = `
          <div class="${className}" role="alert" aria-live="polite">
            <svg class="icon icon-error" aria-hidden="true" focusable="false" viewBox="0 0 13 13" style="width: 1.6rem; height: 1.6rem; margin-right: 0.8rem;">
              <circle cx="6.5" cy="6.50049" r="5.5" stroke="white" stroke-width="2"/>
              <circle cx="6.5" cy="6.5" r="5.5" fill="var(--color-error, #d02e2e)" fill-rule="evenodd"/>
              <path d="m5.87413 3.52832 1.25165-1.25165c.29289-.29289.76777-.29289 1.06066 0l1.25165 1.25165c.29289.29289.29289.76777 0 1.06066l-1.25165 1.25165c-.29289.29289-.76777.29289-1.06066 0l-1.25165-1.25165c-.29289-.29289-.29289-.76777 0-1.06066zm.5 3.5h1v4h-1v-4z" fill="white"/>
            </svg>
            <span class="error-message-text">${message}</span>
          </div>
        `;
      }
      
      /**
       * Show loading state in a container
       * @param {HTMLElement} container - Container element to show loading in
       * @param {string} message - Loading message text
       */
      showLoading(container, message = 'Loading...') {
        if (!container) return;
        container.innerHTML = `
          <div class="loading-state" aria-live="polite" aria-busy="true" style="display: flex; align-items: center; justify-content: center; padding: 2rem; flex-direction: column; gap: 1rem;">
            <div class="loading__spinner" style="width: 2.4rem; height: 2.4rem;">
              <svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                <circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
              </svg>
            </div>
            <span class="loading-message" style="color: rgba(var(--color-foreground), 0.75);">${message}</span>
          </div>
        `;
      }
      
      init() {
        // Ensure color options are loaded from window (set by Liquid template)
        if (window.rimColorOptions) {
          this.rimColors = window.rimColorOptions.rimColors || [];
          this.hubColors = window.rimColorOptions.hubColors || [];
          this.wheelColors = window.rimColorOptions.wheelColors || [];
        }
        
        // Ensure style images are loaded
        if (window.rimImageUrls) {
          this.styleImages = window.rimImageUrls;
        }
        
        this.bindEvents();
        this.populateYearOptions();
        this.updatePreview();
        this.loadWheels(); // Load wheels immediately
        this.populateOptionSelects(); // Load options immediately
        this.calculatePrice(); // Calculate initial price
        
        // Reload products if collection becomes available
        setTimeout(() => {
          if (this.availableProducts.length === 0) {
            this.loadProductsFromCollection();
          }
        }, 2000);
      }
      
      bindEvents() {
        // Vehicle selection events
        this.querySelector('#vehicle-make').addEventListener('change', (e) => {
          this.updateModelOptions(e.target.value);
          this.updatePreview();
        });
        
        this.querySelector('#vehicle-model').addEventListener('change', (e) => {
          this.updateYearOptions(e.target.value);
          this.updatePreview();
        });
        
        this.querySelector('#vehicle-year').addEventListener('change', (e) => {
          this.updatePreview();
        });
        
        // Add to cart
        this.querySelector('#add-to-cart-btn').addEventListener('click', () => {
          this.addToCart();
        });

        // Quantity controls
        this.querySelector('#quantity-minus').addEventListener('click', () => {
          this.updateQuantity(-1);
        });

        this.querySelector('#quantity-plus').addEventListener('click', () => {
          this.updateQuantity(1);
        });

        this.querySelector('#quantity-selector').addEventListener('change', (e) => {
          this.quantity = parseInt(e.target.value) || 1;
          this.calculatePrice();
        });

        // Logo upload events
        this.querySelector('#logo-upload').addEventListener('change', (e) => {
          this.handleLogoUpload(e.target.files[0]);
        });

        this.querySelector('#logo-remove-btn').addEventListener('click', () => {
          this.removeLogo();
        });

        // Wheel position selection (for single wheel) - use event delegation
        this.addEventListener('click', (e) => {
          if (e.target.closest('.wheel-position-option')) {
            const option = e.target.closest('.wheel-position-option');
            const position = option.dataset.position;
            this.selectWheelPosition(position);
          }
        });
        
        // Color dropdown changes
        this.querySelector('#rim-color-select').addEventListener('change', (e) => {
          this.selectedRimColor = e.target.value;
          // If rim/hub is selected, clear wheel color
          if (this.selectedRimColor || this.selectedHubColor) {
            this.selectedWheelColor = '';
            this.querySelector('#wheel-color-select').value = '';
          }
          this.updateStyleFromColors();
        });
        
        this.querySelector('#hub-color-select').addEventListener('change', (e) => {
          this.selectedHubColor = e.target.value;
          // If rim/hub is selected, clear wheel color
          if (this.selectedRimColor || this.selectedHubColor) {
            this.selectedWheelColor = '';
            this.querySelector('#wheel-color-select').value = '';
          }
          this.updateStyleFromColors();
        });
        
        this.querySelector('#wheel-color-select').addEventListener('change', (e) => {
          this.selectedWheelColor = e.target.value;
          // If wheel color is selected, clear rim/hub selections
          if (this.selectedWheelColor) {
            this.selectedRimColor = '';
            this.selectedHubColor = '';
            this.querySelector('#rim-color-select').value = '';
            this.querySelector('#hub-color-select').value = '';
          }
          this.updateStyleFromColors();
        });
      }
      
      populateYearOptions() {
        const yearSelect = this.querySelector('#vehicle-year');
        const currentYear = new Date().getFullYear();
        
        for (let year = currentYear; year >= 2019; year--) {
          const option = document.createElement('option');
          option.value = year;
          option.textContent = year;
          yearSelect.appendChild(option);
        }
      }
      
      updateModelOptions(make) {
        const modelSelect = this.querySelector('#vehicle-model');
        const yearSelect = this.querySelector('#vehicle-year');
        
        // Clear existing options
        modelSelect.innerHTML = '<option value="">Select Model</option>';
        yearSelect.innerHTML = '<option value="">Select Year</option>';
        
        if (make && this.fitmentData[make]) {
          Object.keys(this.fitmentData[make]).forEach(model => {
            const option = document.createElement('option');
            option.value = model;
            option.textContent = model;
            modelSelect.appendChild(option);
          });
        }
        
        this.updatePreview();
      }
      
      updateYearOptions(model) {
        const yearSelect = this.querySelector('#vehicle-year');
        const make = this.querySelector('#vehicle-make').value;
        
        // Clear existing options
        yearSelect.innerHTML = '<option value="">Select Year</option>';
        
        if (make && model && this.fitmentData[make] && this.fitmentData[make][model]) {
          this.fitmentData[make][model].forEach(year => {
            const option = document.createElement('option');
            option.value = year;
            option.textContent = year;
            yearSelect.appendChild(option);
          });
        }
        
        this.updatePreview();
      }
      
      updatePreview() {
        const year = this.querySelector('#vehicle-year').value;
        const make = this.querySelector('#vehicle-make').value;
        const model = this.querySelector('#vehicle-model').value;
        
        const productTitle = this.querySelector('#product-title');
        const productDescription = this.querySelector('#product-description');
        const configurationSummary = this.querySelector('#configuration-summary');
        const configDetails = this.querySelector('#config-details');
        
        if (year && make && model) {
          this.selectedVehicle = { year, make, model };
          productTitle.textContent = `${year} ${make} ${model}`;
          productDescription.textContent = `Compatible wheel options for your ${year} ${make} ${model}. Select your preferred wheel setup below.`;
          
          // All sections are always visible - just update content
          this.loadWheels();
        } else if (make && model) {
          productTitle.textContent = `${make} ${model}`;
          productDescription.textContent = `Select the year of your ${make} ${model} to see compatible wheel options.`;
        } else if (make) {
          productTitle.textContent = `${make} Models`;
          productDescription.textContent = `Select your ${make} model to see compatible wheel options.`;
        } else {
          productTitle.textContent = 'Select Your Vehicle';
          productDescription.textContent = 'Choose your E-moto bike to see compatible wheel options and build your perfect setup.';
        }
        
        // All sections are always visible - no progressive disclosure
        // Reset selections when vehicle changes
        this.selectedWheel = null;
        this.selectedTires = { front: null, rear: null };
        this.selectedOptions = {};
      }
      
      async loadWheels() {
        const wheelGrid = this.querySelector('#wheel-grid');
        if (!wheelGrid) return;
        
        this.showLoading(wheelGrid, 'Loading wheels...');
        
        try {
          // Load actual products from Shopify collection
          await this.loadProductsFromCollection();
          
          // Show wheel type options
          const wheelTypes = [
            {
              id: 'wheel-1',
              title: 'Single Wheel',
              brand: 'Single Wheel',
              price: 0.00,
              image: window.warp9ImageUrl || '',
              compatible: ['Surron', 'Talaria', 'E Ride Pro', 'Stark Varg', 'Arctic Leopard'],
              sizes: ['16-19', '17-19', '19-19'],
              finishes: ['Black', 'Gold', 'Silver']
            },
            {
              id: 'wheel-2',
              title: '435 Wheel Set',
              brand: '435 Custom',
              price: 1599.00,
              image: window.custom435ImageUrl || '',
              compatible: ['Surron', 'Talaria', 'E Ride Pro', 'Stark Varg', 'Arctic Leopard'],
              sizes: ['16-19', '17-19', '19-19'],
              finishes: ['Custom', 'Black', 'Gold', 'Silver']
            }
          ];
          
          this.renderWheels(wheelTypes);
        } catch (error) {
          console.error('Error loading wheels:', error);
          this.showError(wheelGrid, 'Unable to load wheels. Please refresh the page and try again.');
        }
      }
      
      async loadProductsFromCollection() {
        try {
          // Get collection handle from section settings or use default
          const collectionHandle = 'rim-builder'; // Default collection handle
          
          // Use REST API to fetch products from collection
          const restResponse = await fetch(`/collections/${collectionHandle}/products.json`);
          if (restResponse.ok) {
            const restData = await restResponse.json();
            if (restData.products) {
              this.availableProducts = restData.products.map(product => ({
                id: product.id,
                title: product.title,
                handle: product.handle,
                price: parseFloat(product.variants[0]?.price || 0),
                image: product.featured_image || '',
                variantId: product.variants[0]?.id || null,
                available: product.variants[0]?.available || false
              }));
              console.log(`Loaded ${this.availableProducts.length} products from collection`);
            }
          } else {
            console.warn('Could not load products from collection, continuing without product matching');
            this.availableProducts = [];
          }
        } catch (error) {
          console.error('Error loading products from collection:', error);
          // Continue with empty products array - user can still configure
          this.availableProducts = [];
        }
      }
      
      findProductByStyle(styleString) {
        if (!styleString || !this.availableProducts.length) {
          return null;
        }
        
        // Normalize the style string for matching
        const normalize = (str) => str.toLowerCase()
          .replace(/\s+/g, ' ')
          .replace(/[\/]/g, ' / ')
          .replace(/\s*\/\s*/g, ' / ')
          .trim();
        
        const normalizedStyle = normalize(styleString);
        
        // Try exact match first
        let product = this.availableProducts.find(p => 
          normalize(p.title) === normalizedStyle
        );
        
        if (product) return product;
        
        // Try partial match (handle variations in formatting)
        product = this.availableProducts.find(p => {
          const normalizedTitle = normalize(p.title);
          return normalizedTitle === normalizedStyle || 
                 normalizedTitle.includes(normalizedStyle) ||
                 normalizedStyle.includes(normalizedTitle);
        });
        
        if (product) return product;
        
        // Try matching by extracting color names from "COLOR Rim / COLOR Hub" format
        const rimHubMatch = styleString.match(/(\w+)\s+rim\s*\/\s*(\w+)\s+hub/i);
        if (rimHubMatch) {
          const rimColor = rimHubMatch[1].toLowerCase();
          const hubColor = rimHubMatch[2].toLowerCase();
          product = this.availableProducts.find(p => {
            const title = p.title.toLowerCase();
            return title.includes(rimColor) && title.includes(hubColor);
          });
        }
        
        if (product) return product;
        
        // Try matching single color (rim or hub or wheel)
        const singleColorMatch = styleString.match(/(\w+)\s+(?:rim|hub|wheel)/i);
        if (singleColorMatch) {
          const color = singleColorMatch[1].toLowerCase();
          product = this.availableProducts.find(p => 
            normalize(p.title).includes(color)
          );
        }
        
        return product || null;
      }
      
      updateProductMatchStatus() {
        const styleSection = this.querySelector('.style-section');
        if (!styleSection) return;
        
        // Remove existing status indicator
        const existingStatus = styleSection.querySelector('.product-match-status');
        if (existingStatus) {
          existingStatus.remove();
        }
        
        // Add status indicator if style is selected
        if (this.selectedStyle && this.selectedStyle !== 'Select colors') {
          const statusDiv = document.createElement('div');
          statusDiv.className = 'product-match-status';
          
          if (this.matchedProduct) {
            statusDiv.innerHTML = `
              <span class="match-indicator match-found">
                ✓ Product found: ${this.matchedProduct.title}
              </span>
            `;
            statusDiv.classList.add('match-found');
          } else {
            statusDiv.innerHTML = `
              <span class="match-indicator match-not-found">
                ⚠ Product not found for this style. Please contact us for availability.
              </span>
            `;
            statusDiv.classList.add('match-not-found');
          }
          
          styleSection.appendChild(statusDiv);
        }
      }
      
      renderWheels(wheels) {
        const wheelGrid = this.querySelector('#wheel-grid');
        wheelGrid.innerHTML = '';
        
        wheels.forEach(wheel => {
          const wheelCard = document.createElement('div');
          wheelCard.className = 'wheel-card';
          wheelCard.dataset.wheelId = wheel.id;
          
          wheelCard.innerHTML = `
            <img src="${wheel.image}" alt="${wheel.title}" class="wheel-card__image">
            <div class="wheel-card__title">${wheel.title}</div>
            <div class="wheel-card__brand">${wheel.brand}</div>
            <div class="wheel-card__price">$${wheel.price.toFixed(2)}</div>
            <div class="wheel-card__checkmark">✓</div>
          `;
          
          wheelCard.addEventListener('click', () => {
            this.selectWheel(wheel);
          });
          
          wheelGrid.appendChild(wheelCard);
        });
      }
      
      selectWheel(wheel) {
        // Remove previous selection
        this.querySelectorAll('.wheel-card').forEach(card => {
          card.classList.remove('selected');
        });
        
        // Select new wheel
        this.querySelector(`[data-wheel-id="${wheel.id}"]`).classList.add('selected');
        this.selectedWheel = wheel;
        
        // Reset selections when wheel changes
        this.selectedTires = { front: null, rear: null };
        this.selectedOptions = {};
        this.selectedWheelPosition = null;
        this.customizeBoth = false;
        
        // Show/hide position selection based on wheel type
        this.updateWheelTypeUI();
        
        // Update preview
        this.updateProductPreview();
        
        // All sections are always visible - just update content
        this.loadTires();
        this.populateOptionSelects();
        this.calculatePrice();
      }
      
      updateWheelTypeUI() {
        const wheelPositionSection = this.querySelector('#wheel-position-section');
        const customizeBothSection = this.querySelector('#customize-both-section');
        const customizeBothCheckbox = this.querySelector('#customize-both-checkbox');
        
        if (this.selectedWheel && this.selectedWheel.title === 'Single Wheel') {
          // Show front/rear selection for single wheel
          wheelPositionSection.style.display = 'block';
          customizeBothSection.style.display = 'none';
          if (customizeBothCheckbox) {
            customizeBothCheckbox.checked = false;
          }
          this.customizeBoth = false;
        } else if (this.selectedWheel && this.selectedWheel.title === '435 Wheel Set') {
          // Show customize both option for wheel set
          wheelPositionSection.style.display = 'none';
          customizeBothSection.style.display = 'block';
          this.selectedWheelPosition = null;
          // Ensure checkbox event listener is set up
          if (customizeBothCheckbox && !customizeBothCheckbox.hasAttribute('data-listener-attached')) {
            customizeBothCheckbox.setAttribute('data-listener-attached', 'true');
            customizeBothCheckbox.addEventListener('change', (e) => {
              this.customizeBoth = e.target.checked;
              this.updateCustomizationUI();
            });
          }
        } else {
          // Hide both if no wheel selected
          wheelPositionSection.style.display = 'none';
          customizeBothSection.style.display = 'none';
        }
      }
      
      selectWheelPosition(position) {
        // Remove previous selection
        this.querySelectorAll('.wheel-position-option').forEach(option => {
          option.classList.remove('selected');
        });
        
        // Select new position
        this.querySelector(`[data-position="${position}"]`).classList.add('selected');
        this.selectedWheelPosition = position;
        
        // Update preview
        this.updateProductPreview();
      }
      
      updateCustomizationUI() {
        // This can be used to show/hide separate customization options for front/rear
        // when customize both is checked
        if (this.customizeBoth) {
          // Could add separate style/tire options for front and rear here
          console.log('Customize both enabled - separate customization options could be shown here');
        }
        this.updateProductPreview();
      }
      
      updateProductPreview() {
        const productImage = this.querySelector('#main-product-image');
        const productTitle = this.querySelector('#product-title');
        const configurationSummary = this.querySelector('#configuration-summary');
        const configDetails = this.querySelector('#config-details');
        
        if (this.selectedWheel) {
          // Use the selected style image instead of wheel image
          const styleImagePath = this.styleImages[this.selectedStyle];
          if (styleImagePath) {
            productImage.src = styleImagePath;
            productImage.alt = `${this.selectedStyle} Wheel Style`;
          } else {
            productImage.src = this.selectedWheel.image;
            productImage.alt = this.selectedWheel.title;
          }
          
          productTitle.textContent = `${this.selectedWheel.brand} Complete Setup`;
          
          // Check if vehicle is properly selected
          const vehicleText = (this.selectedVehicle.year && this.selectedVehicle.make && this.selectedVehicle.model) 
            ? `${this.selectedVehicle.year} ${this.selectedVehicle.make} ${this.selectedVehicle.model}`
            : 'Please select your vehicle';
          
          let details = `
            <strong>Vehicle:</strong> ${vehicleText}<br>
            <strong>Wheels:</strong> ${this.selectedWheel.title}<br>
          `;
          
          // Add wheel position for single wheel
          if (this.selectedWheel.title === 'Single Wheel' && this.selectedWheelPosition) {
            details += `<strong>Position:</strong> ${this.selectedWheelPosition.charAt(0).toUpperCase() + this.selectedWheelPosition.slice(1)} Wheel<br>`;
          }
          
          // Add customize both status for wheel set
          if (this.selectedWheel.title === '435 Wheel Set') {
            details += `<strong>Customization:</strong> ${this.customizeBoth ? 'Customize Both Separately' : 'Same for Both Wheels'}<br>`;
          }
          
          // Build style details from color selections
          let styleDetails = '';
          if (this.selectedWheelColor) {
            styleDetails = `${this.selectedWheelColor} Wheel`;
          } else if (this.selectedRimColor && this.selectedHubColor) {
            styleDetails = `${this.selectedRimColor} Rim / ${this.selectedHubColor} Hub`;
          } else if (this.selectedRimColor) {
            styleDetails = `${this.selectedRimColor} Rim`;
          } else if (this.selectedHubColor) {
            styleDetails = `${this.selectedHubColor} Hub`;
          } else {
            styleDetails = 'Not selected';
          }
          
          details += `
            <strong>Style:</strong> ${styleDetails}<br>
            <strong>Tires:</strong> ${this.selectedTireOption}
          `;
          
          if (this.uploadedLogo) {
            details += `<br><strong>Custom Logo:</strong> ${this.uploadedLogo.name}`;
          }
          
          configDetails.innerHTML = details;
          configurationSummary.style.display = 'block';
        } else {
          // Show default style image when no wheel is selected
          const styleImagePath = this.styleImages[this.selectedStyle];
          if (styleImagePath) {
            productImage.src = styleImagePath;
            productImage.alt = `${this.selectedStyle} Wheel Style`;
          }
        }
      }

      updateProductDetails() {
        const productDetailsSection = this.querySelector('#product-details-section');
        const productDescriptionContent = this.querySelector('#product-description-content');
        const compatibilityNotes = this.querySelector('#compatibility-notes');
        const includedList = this.querySelector('#included-list');
        const tireOptionsTable = this.querySelector('#tire-options-table');
        
        if (this.selectedWheel) {
          // Product details section is always visible
          
          // Check if vehicle is properly selected for description
          const vehicleName = (this.selectedVehicle.year && this.selectedVehicle.make && this.selectedVehicle.model) 
            ? `${this.selectedVehicle.year} ${this.selectedVehicle.make} ${this.selectedVehicle.model}`
            : 'your E-moto bike';
          
          // Product description
          productDescriptionContent.innerHTML = `
            <p>Upgrade your ${vehicleName} with this premium Warp9 wheelset featuring an anodized finish. This complete wheelset includes everything you need for a ready-to-ride setup.</p>
            <p>The wheelset is compatible with ${vehicleName} models and comes with your choice of tires for a complete package. If no tires are selected, the wheelset ships without them.</p>
            <p>Experience improved performance, lightweight feel, and enhanced traction and handling with this professional-grade wheelset.</p>
          `;
          
          // Compatibility notes
          compatibilityNotes.innerHTML = `
            <h4>Compatibility Notes:</h4>
            <ul>
              <li>Using an adapter for Talaria wheels will void warranty</li>
              <li>Surron LBX/S models require brake rotor adaptors to utilize 220mm rotors</li>
              <li><a href="#">Front 220mm Brake Adaptor</a></li>
              <li><a href="#">Rear 220mm Brake Adaptor</a></li>
            </ul>
          `;
          
          // Included items
          includedList.innerHTML = `
            <li>16x1.85" Warp9 rear wheel</li>
            <li>19x1.6" Warp9 front wheel</li>
            <li>54t Warp9 sprocket (<a href="#">get matching chain here</a>)</li>
            <li>Brake rotors (MTB standard rotor spacing) and adapters</li>
            <li>Wheel color combinations noted as RIM--HUB--SPOKE--NIPPLE</li>
          `;
          
          // Tire options table
          tireOptionsTable.innerHTML = `
            <table>
              <thead>
                <tr>
                  <th>Tire Option</th>
                  <th>Tires</th>
                </tr>
              </thead>
              <tbody>
                ${this.tireOptions.map(tire => `
                  <tr>
                    <td>${tire.name}</td>
                    <td>${tire.description}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          `;
        } else {
          // Product details section is always visible - just show default content
          productDescriptionContent.innerHTML = `
            <p>Select a wheel option to see detailed product information, compatibility notes, and included items.</p>
          `;
          compatibilityNotes.innerHTML = '';
          includedList.innerHTML = '';
          tireOptionsTable.innerHTML = '';
        }
      }
      
      async loadTires() {
        const frontTireGrid = this.querySelector('#front-tire-grid');
        const rearTireGrid = this.querySelector('#rear-tire-grid');
        
        if (!frontTireGrid || !rearTireGrid) return;
        
        this.showLoading(frontTireGrid, 'Loading front tires...');
        this.showLoading(rearTireGrid, 'Loading rear tires...');
        
        try {
          // Mock tire data
          const mockTires = {
            front: [
              {
                id: 'tire-front-1',
                title: 'Shinko SR241',
                size: '80/100-16',
                price: 45.00,
                image: 'https://images.unsplash.com/photo-1606016159991-0d2d8c838692?w=200&h=120&fit=crop&crop=center&auto=format&q=80'
              },
              {
                id: 'tire-front-2',
                title: 'Maxxis MaxxCross',
                size: '80/100-16',
                price: 55.00,
                image: 'https://images.unsplash.com/photo-1606016159991-0d2d8c838692?w=200&h=120&fit=crop&crop=center&auto=format&q=80&sat=1.2'
              }
            ],
            rear: [
              {
                id: 'tire-rear-1',
                title: 'Shinko SR241',
                size: '100/90-19',
                price: 50.00,
                image: 'https://images.unsplash.com/photo-1606016159991-0d2d8c838692?w=200&h=120&fit=crop&crop=center&auto=format&q=80&hue=20'
              },
              {
                id: 'tire-rear-2',
                title: 'Maxxis MaxxCross',
                size: '100/90-19',
                price: 60.00,
                image: 'https://images.unsplash.com/photo-1606016159991-0d2d8c838692?w=200&h=120&fit=crop&crop=center&auto=format&q=80&sat=1.5'
              }
            ]
          };
          
          this.renderTires(mockTires.front, frontTireGrid, 'front');
          this.renderTires(mockTires.rear, rearTireGrid, 'rear');
        } catch (error) {
          console.error('Error loading tires:', error);
          this.showError(frontTireGrid, 'Unable to load front tires. Please try again.');
          this.showError(rearTireGrid, 'Unable to load rear tires. Please try again.');
        }
      }
      
      renderTires(tires, container, type) {
        container.innerHTML = '';
        
        tires.forEach(tire => {
          const tireCard = document.createElement('div');
          tireCard.className = 'tire-card';
          tireCard.dataset.tireId = tire.id;
          
          tireCard.innerHTML = `
            <img src="${tire.image}" alt="${tire.title}" class="tire-card__image">
            <div class="tire-card__title">${tire.title}</div>
            <div class="tire-card__size">${tire.size}</div>
            <div class="tire-card__price">$${tire.price.toFixed(2)}</div>
          `;
          
          tireCard.addEventListener('click', () => {
            this.selectTire(tire, type);
          });
          
          container.appendChild(tireCard);
        });
      }
      
      selectTire(tire, type) {
        // Remove previous selection
        this.querySelectorAll(`#${type}-tire-grid .tire-card`).forEach(card => {
          card.classList.remove('selected');
        });
        
        // Select new tire
        this.querySelector(`[data-tire-id="${tire.id}"]`).classList.add('selected');
        this.selectedTires[type] = tire;
        
        // Update preview
        this.updateProductPreview();
        
        // All sections are always visible - just update content
        this.calculatePrice();
      }
      
      populateOptionSelects() {
        this.populateColorDropdowns();
        this.renderTireOptions();
        this.updateProductDetails();
      }

      populateColorDropdowns() {
        const rimSelect = this.querySelector('#rim-color-select');
        const hubSelect = this.querySelector('#hub-color-select');
        const wheelSelect = this.querySelector('#wheel-color-select');
        
        // Populate rim colors
        if (rimSelect && this.rimColors) {
          this.rimColors.forEach(color => {
            const option = document.createElement('option');
            option.value = color;
            option.textContent = color;
            rimSelect.appendChild(option);
          });
        }
        
        // Populate hub colors
        if (hubSelect && this.hubColors) {
          this.hubColors.forEach(color => {
            const option = document.createElement('option');
            option.value = color;
            option.textContent = color;
            hubSelect.appendChild(option);
          });
        }
        
        // Populate wheel colors
        if (wheelSelect && this.wheelColors) {
          this.wheelColors.forEach(color => {
            const option = document.createElement('option');
            option.value = color;
            option.textContent = color;
            wheelSelect.appendChild(option);
          });
        }
      }

      updateStyleFromColors() {
        const selectedStyleSpan = this.querySelector('#selected-style');
        const previewContainer = this.querySelector('#style-preview-container');
        const previewImage = this.querySelector('#style-preview-image');
        
        // Build style string from selections
        let styleString = '';
        
        if (this.selectedWheelColor) {
          // If wheel color is selected, use that
          styleString = `${this.selectedWheelColor} Wheel`;
          this.selectedStyle = styleString;
        } else if (this.selectedRimColor && this.selectedHubColor) {
          // Rim + Hub combination
          styleString = `${this.selectedRimColor} Rim / ${this.selectedHubColor} Hub`;
          this.selectedStyle = styleString;
        } else if (this.selectedRimColor) {
          styleString = `${this.selectedRimColor} Rim`;
          this.selectedStyle = styleString;
        } else if (this.selectedHubColor) {
          styleString = `${this.selectedHubColor} Hub`;
          this.selectedStyle = styleString;
        } else {
          styleString = 'Select colors';
          this.selectedStyle = '';
        }
        
        selectedStyleSpan.textContent = styleString;
        
        // Find matching product
        this.matchedProduct = this.findProductByStyle(this.selectedStyle);
        
        // Update product match status
        this.updateProductMatchStatus();
        
        // Update preview image
        if (this.selectedStyle && this.styleImages) {
          const imageUrl = this.styleImages[this.selectedStyle];
          if (imageUrl) {
            previewImage.src = imageUrl;
            previewImage.alt = this.selectedStyle;
            previewContainer.style.display = 'block';
          } else if (this.matchedProduct && this.matchedProduct.image) {
            // Use product image if available
            previewImage.src = this.matchedProduct.image;
            previewImage.alt = this.matchedProduct.title;
            previewContainer.style.display = 'block';
          } else {
            // Use placeholder if no specific image
            previewImage.src = window.warp9ImageUrl || '';
            previewContainer.style.display = 'block';
          }
        } else {
          previewContainer.style.display = 'none';
        }
        
        // Update main product preview
        this.updateProductPreview();
        
        // Recalculate price
        this.calculatePrice();
      }

      renderTireOptions() {
        const tireGrid = this.querySelector('#tire-options-grid');
        const selectedTireSpan = this.querySelector('#selected-tire');
        
        tireGrid.innerHTML = '';
        
        this.tireOptions.forEach(tire => {
          const tireOption = document.createElement('div');
          tireOption.className = 'tire-option-btn';
          tireOption.innerHTML = `
            <div class="tire-name">${tire.name}</div>
            <div class="tire-price">${tire.price > 0 ? `+$${tire.price}` : 'Included'}</div>
          `;
          
          if (tire.name === this.selectedTireOption) {
            tireOption.classList.add('selected');
          }
          
          tireOption.addEventListener('click', () => {
            this.selectTireOption(tire);
          });
          
          tireGrid.appendChild(tireOption);
        });
        
        selectedTireSpan.textContent = this.selectedTireOption;
      }

      selectStyle(style) {
        // This method is kept for backward compatibility but may not be used with dropdowns
        this.selectedStyle = style;
        this.updateProductPreview(); // Update main product image
        this.calculatePrice();
      }

      selectTireOption(tire) {
        this.selectedTireOption = tire.name;
        this.renderTireOptions();
        this.calculatePrice();
      }

      updateQuantity(change) {
        const newQuantity = this.quantity + change;
        if (newQuantity >= 1 && newQuantity <= 10) {
          this.quantity = newQuantity;
          this.querySelector('#quantity-selector').value = this.quantity;
          this.calculatePrice();
        }
      }
      
      calculatePrice() {
        let total = 0;
        const priceBreakdown = this.querySelector('#price-breakdown');
        const totalPriceElement = this.querySelector('#total-price');
        const btnPriceElement = this.querySelector('#btn-price');
        
        priceBreakdown.innerHTML = '';
        
        // Use matched product price if available, otherwise use wheel base price
        if (this.matchedProduct && this.selectedStyle) {
          total += this.matchedProduct.price;
          this.addPriceItem(priceBreakdown, this.matchedProduct.title, this.matchedProduct.price);
        } else if (this.selectedWheel) {
          total += this.selectedWheel.price;
          this.addPriceItem(priceBreakdown, 'Warp9 Wheelset', this.selectedWheel.price);
        }
        
        // Tire option price
        const selectedTire = this.tireOptions.find(t => t.name === this.selectedTireOption);
        if (selectedTire && selectedTire.price > 0) {
          total += selectedTire.price;
          this.addPriceItem(priceBreakdown, selectedTire.name, selectedTire.price);
        }
        
        // Apply quantity
        total *= this.quantity;
        
        this.totalPrice = total;
        totalPriceElement.textContent = `$${total.toFixed(2)}`;
        btnPriceElement.textContent = `$${total.toFixed(2)}`;
        
        // Enable add to cart button if wheel and style are selected
        const addToCartBtn = this.querySelector('#add-to-cart-btn');
        const hasValidSelection = this.selectedWheel && 
                                  this.selectedStyle && 
                                  this.selectedStyle !== 'Select colors' &&
                                  (this.matchedProduct || (this.selectedWheel.price > 0 && total > 0));
        
        if (hasValidSelection) {
          addToCartBtn.disabled = false;
          addToCartBtn.classList.remove('btn--disabled');
        } else {
          addToCartBtn.disabled = true;
          addToCartBtn.classList.add('btn--disabled');
        }
        
        // Show warning if style selected but no product matched
        if (this.selectedStyle && this.selectedStyle !== 'Select colors' && !this.matchedProduct && this.availableProducts.length > 0) {
          const warningMsg = 'Note: Exact product match not found. Please verify availability before ordering.';
          if (!this.querySelector('.product-match-warning')) {
            const warning = document.createElement('div');
            warning.className = 'product-match-warning';
            warning.textContent = warningMsg;
            warning.style.cssText = 'margin-top: 0.5rem; padding: 0.5rem; background: rgba(251, 191, 36, 0.1); border-radius: 4px; font-size: 0.875rem; color: rgb(251, 191, 36);';
            addToCartBtn.parentElement.insertBefore(warning, addToCartBtn);
          }
        } else {
          const warning = this.querySelector('.product-match-warning');
          if (warning) warning.remove();
        }
      }
      
      addPriceItem(container, label, price) {
        const priceItem = document.createElement('div');
        priceItem.className = 'price-item';
        priceItem.innerHTML = `
          <span>${label}</span>
          <span>$${price.toFixed(2)}</span>
        `;
        container.appendChild(priceItem);
      }
      
      handleLogoUpload(file) {
        if (!file) return;
        
        // Validate file type
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
        if (!validTypes.includes(file.type)) {
          alert('Please upload a valid image file (PNG, JPG, or SVG)');
          return;
        }
        
        // Validate file size (5MB max)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
          alert('File size must be less than 5MB');
          return;
        }
        
        // Store the file
        this.uploadedLogo = file;
        
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          const previewContainer = this.querySelector('#logo-preview-container');
          const previewImage = this.querySelector('#logo-preview');
          const uploadLabel = this.querySelector('.logo-upload-label-btn');
          
          previewImage.src = e.target.result;
          previewContainer.style.display = 'flex';
          uploadLabel.style.display = 'none';
          
          // Update configuration summary
          if (this.selectedWheel) {
            this.updateProductPreview();
          }
        };
        reader.readAsDataURL(file);
      }
      
      removeLogo() {
        this.uploadedLogo = null;
        const previewContainer = this.querySelector('#logo-preview-container');
        const uploadLabel = this.querySelector('.logo-upload-label-btn');
        const fileInput = this.querySelector('#logo-upload');
        
        previewContainer.style.display = 'none';
        uploadLabel.style.display = 'flex';
        fileInput.value = ''; // Reset file input
        
        // Update configuration summary
        if (this.selectedWheel) {
          this.updateProductPreview();
        }
      }
      
      async addToCart() {
        const addToCartBtn = this.querySelector('#add-to-cart-btn');
        const btnText = addToCartBtn.querySelector('.btn-text');
        
        // Show loading state
        addToCartBtn.disabled = true;
        btnText.textContent = 'Adding to Cart...';
        
        try {
          // Use matched product variant ID if available
          let variantId = null;
          if (this.matchedProduct && this.matchedProduct.variantId) {
            // Extract numeric ID from GID format: gid://shopify/ProductVariant/123456
            const gidMatch = this.matchedProduct.variantId.match(/\d+$/);
            variantId = gidMatch ? gidMatch[0] : this.matchedProduct.variantId;
          }
          
          if (!variantId) {
            throw new Error('No product variant found for selected style. Please ensure all options are selected (vehicle, wheel, and style).');
          }
          
          // Build cart data
          const selectedTire = this.tireOptions.find(t => t.name === this.selectedTireOption);
          const cartData = {
            id: variantId,
            quantity: this.quantity,
            properties: {
              '_Vehicle': `${this.selectedVehicle.year} ${this.selectedVehicle.make} ${this.selectedVehicle.model}`,
              '_Wheel_Brand': this.selectedWheel.brand,
              '_Wheel_Set': this.selectedWheel.title,
              '_Style': this.selectedStyle,
              '_Tire_Option': this.selectedTireOption,
              '_Tire_Description': selectedTire ? selectedTire.description : 'None',
              '_Quantity': this.quantity.toString(),
              '_Total_Price': `$${this.totalPrice.toFixed(2)}`
            }
          };
          
          // Add wheel position or customize both information
          if (this.selectedWheel.title === 'Single Wheel' && this.selectedWheelPosition) {
            cartData.properties['_Wheel_Position'] = this.selectedWheelPosition.charAt(0).toUpperCase() + this.selectedWheelPosition.slice(1);
          } else if (this.selectedWheel.title === '435 Wheel Set') {
            cartData.properties['_Customize_Both'] = this.customizeBoth ? 'Yes' : 'No';
          }
          
          // Add logo information if uploaded
          if (this.uploadedLogo) {
            cartData.properties['_Custom_Logo'] = this.uploadedLogo.name;
            cartData.properties['_Logo_File_Size'] = `${(this.uploadedLogo.size / 1024).toFixed(2)} KB`;
            // In a real implementation, you would upload the file to Shopify's Files API
            // and store the file URL here
          }
          
          // Use Shopify Cart API
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(cartData)
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.description || 'Failed to add item to cart');
          }
          
          // Show success message
          btnText.textContent = 'Added to Cart!';
          
          // Track GA4 add_to_cart event
          if (window.ga4Analytics && this.matchedProduct) {
            window.ga4Analytics.trackAddToCart({
              id: variantId,
              product_id: this.matchedProduct.productId || variantId,
              name: this.matchedProduct.title || 'Custom Wheel',
              price: this.totalPrice,
              quantity: this.quantity,
              currency: window.Shopify?.currency?.active || 'USD',
              category: 'Custom Wheels'
            });
          }
          
          // Dispatch custom event for cart updates
          document.dispatchEvent(new CustomEvent('cart:updated'));
          
          // Reset after 2 seconds
          setTimeout(() => {
            btnText.textContent = 'Add to Cart';
            addToCartBtn.disabled = false;
          }, 2000);
          
        } catch (error) {
          console.error('Error adding to cart:', error);
          const errorMessage = error.message || 'Unable to add item to cart. Please try again.';
          btnText.textContent = errorMessage;
          
          // Show error message in button and optionally in a separate error display
          addToCartBtn.classList.add('error');
          
          setTimeout(() => {
            btnText.textContent = 'Add to Cart';
            addToCartBtn.classList.remove('error');
            addToCartBtn.disabled = false;
          }, 4000);
        }
      }
    }
  );
}